import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import Pagination from '../../../components/common/Pagination';

const SCENARIOS = [
  { value: 'all', label: '시나리오 전체' },
  { value: 'realtor_qr_move', label: '이사 부동산 QR 이동' },
  { value: 'moving_category', label: '이사 구분 안내' },
  { value: 'partner_moving', label: '사업자 이사 안내' },
  { value: 'customer_apply', label: '고객 신청 접수' },
  { value: 'partner_assigned', label: '제휴업체 배정 안내' },
  { value: 'install_complete', label: '설치 완료 안내' },
  { value: 'payment_request', label: '결제 요청' },
  { value: 'payment_complete', label: '결제 완료' },
  { value: 'withdrawal_request', label: '출금 신청' },
  { value: 'withdrawal_done', label: '출금 완료' },
  { value: 'commission_posted', label: '수수료 발생' },
  { value: 'referral_reward', label: '추천 보상' },
  { value: 'notice', label: '일반 공지' },
];

const TYPE_LABELS = {
  SMS: 'SMS',
  sms: 'SMS',
  알림톡: '알림톡',
  kakao: '알림톡',
  alimtalk: '알림톡',
  new_request: '새 신청 푸시',
  status_change: '상태변경 푸시',
  notice: '공지 푸시',
  app_update: '앱 업데이트 푸시',
  test: '테스트 푸시',
  feedback: '피드백',
};

const TYPES = [
  { value: 'all', label: '유형 전체' },
  { value: 'SMS', label: 'SMS' },
  { value: '알림톡', label: '알림톡' },
  { value: 'new_request', label: '새 신청 푸시' },
  { value: 'status_change', label: '상태변경 푸시' },
  { value: 'notice', label: '공지 푸시' },
  { value: 'app_update', label: '앱 업데이트 푸시' },
  { value: 'test', label: '테스트 푸시' },
];

const PER_PAGE = 20;

// 템플릿 코드(레거시 로그 호환) → 한글 라벨
const TEMPLATE_LABELS = {
  signup_complete: '신청 완료 안내',
  partner_assigned: '담당자 배정 안내',
  partner_new_assignment: '제휴업체 신규 배정',
  cancelled: '요청 취소 안내',
  review_request: '서비스 완료(후기 요청)',
  keyword_match: '관심 키워드 매칭',
  db_purchase_restricted: 'DB 마켓 구매 제한',
  realtor_signup_complete: '회원가입 완료',
  realtor_referral_signup: '추천 회원가입 안내',
  commission_daily_update: '오늘의 수수료 변동',
  withdrawal_requested: '출금 요청 접수',
  withdrawal_complete: '출금 완료',
  withdrawal_held: '출금 보류',
  withdrawal_rejected: '출금 반려',
  realtor_invite: '공인중개사 초대',
  partner_signup_complete: '제휴업체 회원가입 완료',
  commission_settled: '수수료 정산 완료',
  system_notice: '시스템 공지',
};
const looksLikeCode = (s) => typeof s === 'string' && /^[a-z][a-z0-9_]*$/.test(s.trim());

const formatDateTime = (d) => {
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const AlimtalkLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scenario, setScenario] = useState('all');
  const [type, setType] = useState('all');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'notification_logs'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const data = snap.docs.map((d) => {
        const raw = d.data();
        const date = raw.createdAt?.toDate?.();
        const successNum = typeof raw.success === 'number' ? raw.success : null;
        const failNum = typeof raw.fail === 'number' ? raw.fail : null;
        const tokenCount = typeof raw.tokenCount === 'number' ? raw.tokenCount : null;
        const statusOk =
          raw.status === 'success' ||
          raw.status === 'sent' ||
          raw.success === true ||
          (successNum !== null && successNum > 0);
        return {
          id: d.id,
          phone: raw.phone || raw.receiver || '-',
          scenarioKey: raw.scenario || raw.scenarioKey || '',
          type: raw.type || 'sms',
          channel: raw.channel || '',
          content: (() => {
            const raw1 = raw.content || raw.message || raw.body || raw.title || '';
            // 코드만 저장된 옛날 로그는 한글 라벨로 치환
            if (looksLikeCode(raw1) && TEMPLATE_LABELS[raw1]) return TEMPLATE_LABELS[raw1];
            return raw1 || '-';
          })(),
          templateCode: raw.templateCode || (looksLikeCode(raw.message || '') ? raw.message : null),
          success: statusOk,
          successNum,
          failNum,
          tokenCount,
          date,
        };
      });
      setLogs(data);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, []);

  const filtered = useMemo(() => logs.filter((log) => {
    if (scenario !== 'all' && log.scenarioKey !== scenario) return false;
    if (type !== 'all' && log.type !== type) return false;
    return true;
  }), [logs, scenario, type]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const toggleOne = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const allPagedSelected = paged.length > 0 && paged.every((l) => selected.includes(l.id));
  const toggleAll = () => {
    if (allPagedSelected) {
      setSelected((prev) => prev.filter((id) => !paged.some((l) => l.id === id)));
    } else {
      setSelected((prev) => [...new Set([...prev, ...paged.map((l) => l.id)])]);
    }
  };

  const clearSelection = () => setSelected([]);

  const csvRows = (rows) => {
    const head = ['순번', '연락처', '시나리오', '유형', '내용', '결과', '일시'];
    const body = rows.map((l, i) => [
      i + 1,
      l.phone,
      SCENARIOS.find((s) => s.value === l.scenarioKey)?.label || l.scenarioKey || '-',
      TYPES.find((t) => t.value === l.type)?.label || l.type,
      (l.content || '').replace(/\n/g, ' ').replace(/,/g, '，'),
      l.success ? '성공' : '실패',
      formatDateTime(l.date),
    ]);
    return [head, ...body].map((r) => r.join(',')).join('\n');
  };

  const download = (rows, filename) => {
    const blob = new Blob(['\uFEFF' + csvRows(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAll = () => download(filtered, `알림로그_${Date.now()}.csv`);
  const downloadSelected = () => {
    const rows = logs.filter((l) => selected.includes(l.id));
    download(rows, `알림로그_선택_${Date.now()}.csv`);
  };

  const scenarioLabel = (key) => SCENARIOS.find((s) => s.value === key)?.label || key || '-';
  const typeLabel = (v) => TYPE_LABELS[v] || v;

  return (
    <Container>
      <Header>
        <Title>알림 관리</Title>
        <HeaderActions>
          <TextBtn onClick={downloadAll}>CSV 다운로드</TextBtn>
          <TextBtn onClick={fetchLogs}>새로고침</TextBtn>
        </HeaderActions>
      </Header>

      <FilterRow>
        <Select value={scenario} onChange={(e) => { setScenario(e.target.value); setPage(1); }}>
          {SCENARIOS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </Select>
        <Select value={type} onChange={(e) => { setType(e.target.value); setPage(1); }}>
          {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </Select>
        <SendBtn
          disabled
          title="실시간 발송은 Solapi 연동 후 활성화됩니다"
        >
          발송 실시간 보내기
        </SendBtn>
      </FilterRow>

      {selected.length > 0 && (
        <SelectBar>
          <SelCount>{selected.length}건 선택됨</SelCount>
          <SelActions>
            <TextBtn onClick={downloadSelected}>선택 항목 CSV 다운로드</TextBtn>
            <TextBtn onClick={clearSelection}>선택 해제</TextBtn>
          </SelActions>
        </SelectBar>
      )}

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th style={{ width: 40 }}>
                <Check type="checkbox" checked={allPagedSelected} onChange={toggleAll} />
              </Th>
              <Th style={{ width: 60 }}>순번</Th>
              <Th>연락처</Th>
              <Th>시나리오(KEY)</Th>
              <Th style={{ width: 120 }}>유형</Th>
              <Th>내용 요약</Th>
              <Th style={{ width: 70 }}>결과</Th>
              <Th style={{ width: 160 }}>일시</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><Td colSpan={8} style={{ textAlign: 'center', padding: '40px 0' }}>로딩 중...</Td></tr>
            ) : paged.length === 0 ? (
              <tr><Td colSpan={8} style={{ textAlign: 'center', padding: '40px 0', color: '#9CA3AF' }}>데이터가 없습니다.</Td></tr>
            ) : paged.map((log, i) => (
              <tr key={log.id}>
                <Td><Check type="checkbox" checked={selected.includes(log.id)} onChange={() => toggleOne(log.id)} /></Td>
                <Td style={{ color: '#9CA3AF' }}>{(page - 1) * PER_PAGE + i + 1}</Td>
                <Td>{log.phone}</Td>
                <Td>
                  <ScenarioLabel>{scenarioLabel(log.scenarioKey)}</ScenarioLabel>
                  {log.scenarioKey && <ScenarioKey>{log.scenarioKey}</ScenarioKey>}
                </Td>
                <Td>{typeLabel(log.type)}</Td>
                <Td style={{ maxWidth: 360 }}>
                  <Ellipsis>{log.content}</Ellipsis>
                </Td>
                <Td>
                  <Result
                    $ok={log.success}
                    $neutral={log.tokenCount === 0}
                  >
                    {log.successNum !== null && log.failNum !== null
                      ? (log.tokenCount === 0
                          ? '수신자 없음'
                          : `${log.successNum}/${log.tokenCount ?? (log.successNum + log.failNum)} 성공`)
                      : (log.success ? '성공' : '실패')}
                  </Result>
                </Td>
                <Td style={{ color: '#6B7280' }}>{formatDateTime(log.date)}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Pagination current={page} total={totalPages} onChange={setPage} />
      </TableWrap>
    </Container>
  );
};

export default AlimtalkLog;

const Container = styled.div``;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 16px;
`;

const TextBtn = styled.button`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: none;
  padding: 4px 0;
  &:hover { color: ${({ theme }) => theme.colors.primary}; text-decoration: underline; }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  background: #fff;
  min-width: 160px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

const SendBtn = styled.button`
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  font-weight: 500;
  margin-left: auto;
  &:disabled {
    background: ${({ theme }) => theme.colors.borderLight};
    color: ${({ theme }) => theme.colors.textLight};
    cursor: not-allowed;
  }
`;

const SelectBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: #F9FAFB;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  margin-bottom: 12px;
`;

const SelCount = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
`;

const SelActions = styled.div`
  display: flex;
  gap: 16px;
`;

const TableWrap = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: ${({ theme }) => theme.shadow.sm};
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 13px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  vertical-align: middle;
`;

const Check = styled.input`
  width: 14px;
  height: 14px;
  cursor: pointer;
`;

const ScenarioLabel = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text};
`;

const ScenarioKey = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textLight};
  font-family: 'SF Mono', monospace;
  margin-top: 2px;
`;

const Ellipsis = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Result = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ $ok, $neutral, theme }) =>
    $neutral ? '#9CA3AF' : ($ok ? theme.colors.success : theme.colors.danger)};
  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ $ok, $neutral, theme }) =>
      $neutral ? '#9CA3AF' : ($ok ? theme.colors.success : theme.colors.danger)};
    margin-right: 6px;
    vertical-align: middle;
  }
`;
